import { getEntityMatchScore } from 'utils/nameMatchScore';
import type { Scope } from 'components/ScreeningComposer/types';
import {
  EntityTableFilterSelection,
  buildEntityTableFilterParams,
} from './entityTableFilterParams';

/** Default page size for Entity Screening Hits (Tool and Entity Hub Results). */
export const ENTITY_HITS_PAGE_SIZE = 100;

/**
 * OpenSearch top-N fetched for Tool AI. Matches Daily Screening's default
 * `AI_MAX_DOCS`. Match Score then cuts this set before pyapi.
 */
export const ENTITY_AI_SAMPLE_SIZE = 250;

/** Inclusive Match Score floor for sending an AI-sample hit to pyapi. */
export const ENTITY_AI_EVAL_MIN_MATCH_SCORE = 70;

export const ENTITY_AI_EVAL_NO_STRONG_MATCHES_MESSAGE = `No results in the top matches scored above ${ENTITY_AI_EVAL_MIN_MATCH_SCORE}%, so an evaluation was not run.`;

/** OpenSearch default max_result_window. Pager stops here; footer/tab still show full Hit Total. */
const OPENSEARCH_RESULT_WINDOW = 10000;

export type EntityHitsPagerMath = {
  hitTotal: number;
  pageSize?: number;
};

/**
 * Compact pager page count. Caps at the OpenSearch `from+size` window so the
 * control never offers a jump-to-last beyond what normal paging can fetch
 * (no `… 901`). Tab/footer still use the raw Hit Total separately.
 */
export function entityHitsPageCount(args: EntityHitsPagerMath): number {
  const pageSize = args.pageSize ?? ENTITY_HITS_PAGE_SIZE;
  const safeTotal = Math.max(0, Number(args.hitTotal) || 0);
  const uncapped = Math.max(1, Math.ceil(safeTotal / pageSize));
  const windowCap = Math.max(1, Math.floor(OPENSEARCH_RESULT_WINDOW / pageSize));
  return Math.min(windowCap, uncapped);
}

/**
 * ScoreParams for one interactive Hits page. Never requests a bulk full-set
 * qty — table True Pagination loads one server page at a time.
 */
export function buildInteractiveEntityHitsScoreParams(args: {
  base: Record<string, unknown>;
  pageNum: number;
}): Record<string, unknown> {
  return {
    ...args.base,
    pageNum: Math.max(0, args.pageNum),
    qty: ENTITY_HITS_PAGE_SIZE,
  };
}

/**
 * Whether the compact pager may request this 0-based page.
 * Every page in {@link entityHitsPageCount} is navigable.
 */
export function isEntityHitsPageNavigable(
  args: EntityHitsPagerMath & { pageIndex: number },
): boolean {
  const pageCount = entityHitsPageCount(args);
  return args.pageIndex >= 0 && args.pageIndex < pageCount;
}

/**
 * Next 0-based page for a pager click, or `null` to stay put.
 */
export function resolveEntityHitsPageChange(
  args: EntityHitsPagerMath & {
    requestedPageIndex: number;
    currentPageIndex: number;
  },
): number | null {
  const pageCount = entityHitsPageCount(args);
  const requested = Math.max(
    0,
    Math.min(pageCount - 1, args.requestedPageIndex),
  );

  if (requested === args.currentPageIndex) {
    return null;
  }

  if (
    !isEntityHitsPageNavigable({
      ...args,
      pageIndex: requested,
    })
  ) {
    return null;
  }

  return requested;
}

export type InteractiveHitsDataset = 'entity' | 'ownership' | 'both';

export type InteractiveHitsFetchPlan = {
  entityPageNum: number | null;
  ownershipPageNum: number | null;
};

/**
 * Which Tool datasets to fetch for one submit. Fresh screens load entity +
 * Ownership page 0 (True Pagination — never a 1k bulk). The default browse
 * load keeps its own entity query and does not fetch Ownership — an empty
 * ownership query is match_all against the BOI index and races named
 * screens with multi-million Hit Totals. Pager clicks load only the active tab.
 */
export function planInteractiveToolHitsFetch(args: {
  isFreshScreen: boolean;
  isInitialBrowse?: boolean;
  dataset?: InteractiveHitsDataset;
  pageNum: number;
}): InteractiveHitsFetchPlan {
  const pageNum = Math.max(0, args.pageNum);

  if (args.isFreshScreen) {
    return { entityPageNum: 0, ownershipPageNum: 0 };
  }

  // Default /entity-screening browse is entity-only. Ownership waits for a
  // named screen (or an Ownership-tab pager click after one).
  if (args.isInitialBrowse) {
    return { entityPageNum: null, ownershipPageNum: null };
  }

  const dataset = args.dataset ?? 'both';
  if (dataset === 'entity') {
    return { entityPageNum: pageNum, ownershipPageNum: null };
  }
  if (dataset === 'ownership') {
    return { entityPageNum: null, ownershipPageNum: pageNum };
  }

  return { entityPageNum: pageNum, ownershipPageNum: pageNum };
}

export function interactiveHitsDatasetForTab(
  tab: string,
): Exclude<InteractiveHitsDataset, 'both'> {
  return tab === 'Ownership' ? 'ownership' : 'entity';
}

export type EntityScreenInputMode = 'details' | 'record';

/**
 * Who a screen is about. `country` is context for the AI Evaluation and
 * Beneficial Ownership only — never written back to Country of Interest.
 */
export type EntityScreenSubject = {
  name: string;
  country: string | null;
};

function recordFieldText(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value.find(
      (item) => typeof item === 'string' && item.trim().length > 0,
    );
    return typeof first === 'string' ? first.trim() : '';
  }
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Name mode: the typed name, no country context. Record mode (Record ID
 * Search): the matched record's `Name of Entity` + `Entity Country` — never
 * the ID. `null` when there is nothing to screen (blank name, or record mode
 * with no matched record).
 */
export function resolveEntityScreenSubject(args: {
  inputMode: EntityScreenInputMode;
  primary: string;
  matchedRecord?: Record<string, any> | null;
}): EntityScreenSubject | null {
  if (args.inputMode === 'record') {
    const name = recordFieldText(args.matchedRecord?.['Name of Entity']);
    if (!name) {
      return null;
    }
    const country = recordFieldText(args.matchedRecord?.['Entity Country']);
    return { name: name, country: country || null };
  }

  const name = (args.primary || '').trim();
  return name ? { name: name, country: null } : null;
}

/** The `_source` of the first Hit in one entity search result payload. */
export function topHitSource(result: any): Record<string, any> | null {
  return result?.result?.body?.hits?.hits?.[0]?._source ?? null;
}

export type InteractiveEntityAiEvalPlan =
  | { kind: 'sample' }
  | {
      kind: 'record';
      subject: EntityScreenSubject;
      sources: Record<string, any>[];
    }
  | { kind: 'none' };

/**
 * Name mode: whether the Tool should fetch the separate OpenSearch top-N AI
 * sample. Hit Total does not gate this. The Match Score cut happens after the
 * sample returns. Table True Pagination never supplies the sample.
 *
 * Record mode: evaluate the matched record only, with its name as the
 * subject — no top-N name sample. No matched record, no evaluation.
 */
export function planInteractiveEntityAiEvaluation(args: {
  hasPrimary: boolean;
  inputMode?: EntityScreenInputMode;
  matchedRecord?: Record<string, any> | null;
}): InteractiveEntityAiEvalPlan {
  if (args.inputMode === 'record') {
    const subject = resolveEntityScreenSubject({
      inputMode: 'record',
      primary: '',
      matchedRecord: args.matchedRecord,
    });
    if (!subject || !args.matchedRecord) {
      return { kind: 'none' };
    }
    return { kind: 'record', subject: subject, sources: [args.matchedRecord] };
  }

  if (!args.hasPrimary) {
    return { kind: 'none' };
  }

  return { kind: 'sample' };
}

export function isEligibleInteractiveEntityAiMatchScore(
  score: number | null,
): boolean {
  return (
    typeof score === 'number' && score >= ENTITY_AI_EVAL_MIN_MATCH_SCORE
  );
}

/**
 * Keep AI-sample sources whose Hits-table Match Score is at or above the
 * floor, in the OpenSearch order they arrived. Score on the raw source so
 * alias / remarks fields still count, the same as the table column.
 */
export function selectInteractiveEntityAiSources(args: {
  searchTerm: string;
  sources: Record<string, any>[];
}): Record<string, any>[] {
  return args.sources.filter((source) => {
    if (!source) {
      return false;
    }
    const score = getEntityMatchScore(args.searchTerm, source);
    return isEligibleInteractiveEntityAiMatchScore(score);
  });
}

/**
 * ScoreParams for the AI top-N sample. Independent of table True Pagination:
 * always page 0 and `ENTITY_AI_SAMPLE_SIZE`, never a 1k bulk or the table
 * page size. Keep `agg: true` on `base` — the interactive entity query only
 * runs the name search on that path.
 *
 * When `tableFilters` is passed it replaces any on `base`, with the same
 * post_filter payload the table page sends, so the sample is drawn from the
 * filtered matches. An empty selection sends none.
 */
export function buildInteractiveEntityAiSampleScoreParams(args: {
  base: Record<string, unknown>;
  tableFilters?: EntityTableFilterSelection | null;
}): Record<string, unknown> {
  return firstPageScoreParams(
    args.base,
    ENTITY_AI_SAMPLE_SIZE,
    args.tableFilters,
  );
}

/**
 * Page 0 of `qty` rows over `base`. `tableFilters` replaces any on `base`;
 * `null` or an empty selection sends none, `undefined` keeps `base`'s.
 */
function firstPageScoreParams(
  base: Record<string, unknown>,
  qty: number,
  tableFilters?: EntityTableFilterSelection | null,
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...base, pageNum: 0, qty: qty };
  if (tableFilters !== undefined) {
    const payload = buildEntityTableFilterParams(tableFilters);
    if (payload) {
      params.tableFilters = payload;
    } else {
      delete params.tableFilters;
    }
  }
  return params;
}

/**
 * ScoreParams for the unfiltered Hit Total behind "Filtered: N of M matches".
 * Drops Table Filters and asks for one row: datahub reads `qty: 0` as its
 * 50-row default.
 */
export function buildInteractiveEntityMatchTotalScoreParams(args: {
  base: Record<string, unknown>;
}): Record<string, unknown> {
  return firstPageScoreParams(args.base, 1, null);
}

/** Why a screen is (re-)running. Everything but `newSubject` is the same subject. */
export type EntityScreenRerunReason =
  | 'newSubject'
  | 'rerun'
  | 'archived'
  | 'countryOfInterest'
  | 'asOfDate'
  | 'screenFilters';

/** What a screen was run for: the input mode and its trimmed name or record ID. */
export type EntityScreenQuery = {
  inputMode: EntityScreenInputMode;
  query: string;
};

/**
 * An explicit `requested` reason wins. Otherwise a Run on the query of the
 * last screen is a `rerun`; any other query is a `newSubject`.
 */
export function resolveEntityScreenRerunReason(args: {
  requested?: EntityScreenRerunReason;
  current: EntityScreenQuery;
  last: EntityScreenQuery | null;
}): EntityScreenRerunReason {
  if (args.requested) {
    return args.requested;
  }
  const isSameQuery =
    args.last !== null &&
    args.last.inputMode === args.current.inputMode &&
    args.last.query === args.current.query;
  return isSameQuery ? 'rerun' : 'newSubject';
}

/**
 * How a screen request runs. `runAiEvaluation` (default true) makes it a
 * fresh screen of both datasets; false fetches one page of `dataset`
 * (default `both`). `reason` defaults from the query, see
 * {@link resolveEntityScreenRerunReason}. `scope` is the screen's filters
 * when they were applied in the same tick, before state can be read.
 */
export type EntityScreenRunOptions = {
  runAiEvaluation?: boolean;
  dataset?: InteractiveHitsDataset;
  reason?: EntityScreenRerunReason;
  scope?: Scope;
};
